import { useMemo, useEffect } from 'react';

import { formatUnits } from 'viem';
import { useReadContract } from 'wagmi';

import { poolContractAbi } from '@/abi';
import { useDebouncedBigInt } from '@/hooks/useDebouncedBigInt';
import { formatNumberWithCommasAndDecimals } from '@/lib/utils/formatNumberWithCommasAndDecimals';
import { C_POOL_TOKEN_DECIMALS } from '@/ts/constants/C_DECIMAL_PLACES';
import { C_USDC_DECIMALS } from '@/ts/constants/C_USDC_DECIMALS';

import type { T_SwapDirection } from './usePoolsTokenSwap';

interface I_UsePoolsTokenContractCallsProps {
  poolContractAddress: string;
  swapDirection: T_SwapDirection;
  inputAmountBigInt: bigint | undefined;
  tokenDecimals: number;
  validatedInput: string;
  isInputFocused: boolean;
  isInputPendingCalculation: boolean;
  isUpdatingInputFromCalculation: boolean;
  activeChainId: number;
  setIsInputPendingCalculation: (value: boolean) => void;
  setIsUpdatingInputFromCalculation: (value: boolean) => void;
}

export function usePoolsTokenContractCalls({
  poolContractAddress,
  swapDirection,
  inputAmountBigInt,
  tokenDecimals,
  validatedInput,
  isInputFocused,
  isInputPendingCalculation,
  isUpdatingInputFromCalculation,
  activeChainId,
  setIsInputPendingCalculation,
  setIsUpdatingInputFromCalculation
}: I_UsePoolsTokenContractCallsProps) {
  // Separate BigInt values for USDC and token directions
  const usdcAmountBigInt =
    swapDirection === 'usdc-to-token' ? inputAmountBigInt : undefined;

  // For token-to-usdc, convert to 18 decimals for the contract call
  const tokenAmountBigIntForContract = useMemo(() => {
    if (swapDirection !== 'token-to-usdc' || !inputAmountBigInt)
      return undefined;

    if (tokenDecimals === 18) {
      return inputAmountBigInt;
    } else if (tokenDecimals < 18) {
      return inputAmountBigInt * BigInt(10 ** (18 - tokenDecimals));
    } else {
      return inputAmountBigInt / BigInt(10 ** (tokenDecimals - 18));
    }
  }, [swapDirection, inputAmountBigInt, tokenDecimals]);

  // Debounce BigInt values
  const debouncedUsdcAmountBigInt = useDebouncedBigInt(
    usdcAmountBigInt,
    isUpdatingInputFromCalculation
  );
  const debouncedTokenAmountBigInt = useDebouncedBigInt(
    tokenAmountBigIntForContract,
    isUpdatingInputFromCalculation
  );

  // Get tokens by USDC
  const { data: tokensByUsdcData, isLoading: isLoadingTokens } =
    useReadContract({
      address: poolContractAddress as `0x${string}`,
      abi: poolContractAbi,
      functionName: 'getTokensByUsdc',
      args:
        debouncedUsdcAmountBigInt !== undefined
          ? [debouncedUsdcAmountBigInt]
          : undefined,
      chainId: activeChainId,
      query: {
        enabled:
          !!poolContractAddress &&
          debouncedUsdcAmountBigInt !== undefined &&
          debouncedUsdcAmountBigInt !== 0n &&
          !!validatedInput &&
          !isUpdatingInputFromCalculation &&
          swapDirection === 'usdc-to-token' &&
          (isInputFocused || isInputPendingCalculation)
      }
    }) as { data: [bigint, bigint] | undefined; isLoading: boolean };

  // Get USDC by tokens
  const { data: usdcByTokensData, isLoading: isLoadingUsdc } = useReadContract({
    address: poolContractAddress as `0x${string}`,
    abi: poolContractAbi,
    functionName: 'getUsdcByTokens',
    args:
      debouncedTokenAmountBigInt !== undefined
        ? [debouncedTokenAmountBigInt]
        : undefined,
    chainId: activeChainId,
    query: {
      enabled:
        !!poolContractAddress &&
        debouncedTokenAmountBigInt !== undefined &&
        debouncedTokenAmountBigInt !== 0n &&
        !!validatedInput &&
        !isUpdatingInputFromCalculation &&
        swapDirection === 'token-to-usdc' &&
        (isInputFocused || isInputPendingCalculation)
    }
  }) as { data: [bigint, bigint] | undefined; isLoading: boolean };

  // Handle calculation completion
  useEffect(() => {
    if (swapDirection === 'usdc-to-token') {
      if (tokensByUsdcData && !isLoadingTokens) {
        setIsInputPendingCalculation(false);
        setIsUpdatingInputFromCalculation(false);
      } else if (isLoadingTokens && validatedInput) {
        setIsInputPendingCalculation(true);
      }
    } else {
      if (usdcByTokensData && !isLoadingUsdc) {
        setIsInputPendingCalculation(false);
        setIsUpdatingInputFromCalculation(false);
      } else if (isLoadingUsdc && validatedInput) {
        setIsInputPendingCalculation(true);
      }
    }
  }, [
    tokensByUsdcData,
    usdcByTokensData,
    isLoadingTokens,
    isLoadingUsdc,
    swapDirection,
    validatedInput,
    setIsInputPendingCalculation,
    setIsUpdatingInputFromCalculation
  ]);

  // Calculate output value
  const outputValue = useMemo(() => {
    if (swapDirection === 'usdc-to-token') {
      if (!debouncedUsdcAmountBigInt || debouncedUsdcAmountBigInt === 0n) {
        return formatNumberWithCommasAndDecimals(0, C_POOL_TOKEN_DECIMALS);
      }
      if (tokensByUsdcData) {
        const [tokensAmount] = tokensByUsdcData;
        const tokensFrom18Decimals = formatUnits(tokensAmount, 18);
        return formatNumberWithCommasAndDecimals(
          tokensFrom18Decimals,
          C_POOL_TOKEN_DECIMALS
        );
      }
      return formatNumberWithCommasAndDecimals(0, C_POOL_TOKEN_DECIMALS);
    } else {
      if (!debouncedTokenAmountBigInt || debouncedTokenAmountBigInt === 0n) {
        return formatNumberWithCommasAndDecimals(0, C_USDC_DECIMALS);
      }
      if (usdcByTokensData) {
        const [usdcAmount] = usdcByTokensData;
        const usdcFrom18Decimals = formatUnits(usdcAmount, C_USDC_DECIMALS);
        return formatNumberWithCommasAndDecimals(
          usdcFrom18Decimals,
          C_USDC_DECIMALS
        );
      }
      return formatNumberWithCommasAndDecimals(0, C_USDC_DECIMALS);
    }
  }, [
    debouncedUsdcAmountBigInt,
    debouncedTokenAmountBigInt,
    swapDirection,
    tokensByUsdcData,
    usdcByTokensData
  ]);

  // Calculate fee value
  const feeValue = useMemo(() => {
    if (swapDirection === 'usdc-to-token') {
      if (!debouncedUsdcAmountBigInt || debouncedUsdcAmountBigInt === 0n) {
        return formatNumberWithCommasAndDecimals(0, C_USDC_DECIMALS);
      }
      if (tokensByUsdcData) {
        const [_, feeAmount] = tokensByUsdcData;
        const feeFrom18Decimals = formatUnits(feeAmount, C_USDC_DECIMALS);
        return formatNumberWithCommasAndDecimals(
          feeFrom18Decimals,
          C_USDC_DECIMALS
        );
      }
      return formatNumberWithCommasAndDecimals(0, C_USDC_DECIMALS);
    } else {
      if (!debouncedTokenAmountBigInt || debouncedTokenAmountBigInt === 0n) {
        return formatNumberWithCommasAndDecimals(0, C_USDC_DECIMALS);
      }
      if (usdcByTokensData) {
        const [_, feeAmount] = usdcByTokensData;
        const feeFrom18Decimals = formatUnits(feeAmount, C_USDC_DECIMALS);

        return formatNumberWithCommasAndDecimals(
          feeFrom18Decimals,
          C_USDC_DECIMALS
        );
      }
      return formatNumberWithCommasAndDecimals(0, C_USDC_DECIMALS);
    }
  }, [
    debouncedUsdcAmountBigInt,
    debouncedTokenAmountBigInt,
    swapDirection,
    tokensByUsdcData,
    usdcByTokensData
  ]);

  return {
    outputValue,
    feeValue,
    tokensByUsdcData,
    usdcByTokensData,
    isLoadingTokens,
    isLoadingUsdc
  };
}
